import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../hooks/useAuth';

function MonsterStatBlock({ monster, fileName, canEdit = false, canSave = false, onSave }) {
  const { user, signInWithGoogle } = useAuth();
  const showEdit = canEdit && user;
  const showSave = canSave && user;
  const needsAuth = (canEdit || canSave) && !user;
  const [editPrompt, setEditPrompt] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentMonster, setCurrentMonster] = useState(monster);
  const [editHistory, setEditHistory] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (monster) {
      setCurrentMonster(monster);
      setEditHistory([]);
    }
  }, [monster]);

  const m = currentMonster || monster;
  if (!m) return null;

  const abilities = m.abilities || {};
  const arr = (x) => (Array.isArray(x) ? x : []);
  const join = (x) => (Array.isArray(x) ? x.join(', ') : x);

  async function handleEdit(e) {
    e.preventDefault();
    if (!editPrompt.trim()) return;
    const prompt = editPrompt.trim();
    setEditLoading(true);
    setError(null);
    try {
      const body = editHistory.length > 0
        ? { monster: m, edit_prompt: prompt, history: editHistory }
        : { monster: m, edit_prompt: prompt };
      const { data, err } = await supabase.functions.invoke('edit-monster', { body });
      if (err) throw err;
      if (data?.error) throw new Error(data.error);
      if (data?.monster) {
        setCurrentMonster(data.monster);
        setEditPrompt('');
        const userContent = editHistory.length > 0
          ? `Make these changes: ${prompt}`
          : `Here is the current monster:\n${JSON.stringify(m)}\n\nMake these changes: ${prompt}`;
        setEditHistory((prev) => [
          ...prev,
          { role: 'user', content: userContent },
          { role: 'assistant', content: JSON.stringify(data.monster) },
        ]);
      }
    } catch (e) {
      setError(e.message || 'Edit failed');
    } finally {
      setEditLoading(false);
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaveLoading(true);
    setError(null);
    try {
      const { data, err } = await supabase.functions.invoke('upload-monster', {
        body: { monster: m },
      });
      if (err) throw err;
      if (data?.error) throw new Error(data.error);
      if (data?.filename && !fileName) navigate(`/monsters/${encodeURIComponent(data.filename)}`);
      if (onSave) onSave();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaveLoading(false);
    }
  }

  return (
    <div className="box">
      <div className="stat-block" style={{ fontFamily: 'serif', maxWidth: '400px' }}>
        <div className="title is-4">{m.name}</div>
        <p className="subtitle is-6">
          {m.type} {m.size}
        </p>
        <hr />
        <div className="columns is-mobile is-multiline">
          {['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'].map(
            (k) => (
              <div key={k} className="column is-2">
                <div className="has-text-centered">
                  <div className="is-size-7 has-text-weight-bold">{k.slice(0, 3).toUpperCase()}</div>
                  <div>{abilities[k] ?? '-'}</div>
                </div>
              </div>
            )
          )}
        </div>
        <hr />
        <p>
          <strong>Armor Class</strong> {m.armor_class}
        </p>
        <p>
          <strong>Hit Points</strong> {m.hit_points}
        </p>
        <p>
          <strong>Speed</strong> {m.speed}
        </p>
        {arr(m.skill_proficiencies).length > 0 && (
          <p>
            <strong>Skills</strong>{' '}
            {arr(m.skill_proficiencies)
              .map((s) => {
                const b = String(s.total_bonus ?? '').trim();
                const prefix = b && !/^[+-]/.test(b) ? '+' : '';
                return `${s.name} ${prefix}${b}`;
              })
              .join(', ')}
          </p>
        )}
        {arr(m.damage_vulnerabilities).length > 0 && (
          <p>
            <strong>Damage Vulnerabilities</strong> {join(m.damage_vulnerabilities)}
          </p>
        )}
        {arr(m.damage_resistances).length > 0 && (
          <p>
            <strong>Damage Resistances</strong> {join(m.damage_resistances)}
          </p>
        )}
        {arr(m.damage_immunities).length > 0 && (
          <p>
            <strong>Damage Immunities</strong> {join(m.damage_immunities)}
          </p>
        )}
        {arr(m.passive_abilities).length > 0 && (
          <>
            <hr />
            {arr(m.passive_abilities).map((pa, i) => (
              <p key={i}>
                <strong>{pa.Name}.</strong> {pa.Description}
              </p>
            ))}
          </>
        )}
        {arr(m.actions).length > 0 && (
          <>
            <hr />
            {m.multi_attack && (
              <p>
                <strong>Multiattack.</strong> The creature can make {m.multi_attack} attack(s).
              </p>
            )}
            <p>
              <strong>Actions</strong>
            </p>
            {arr(m.actions).map((a, i) => (
              <p key={i}>
                <strong>{a.name}.</strong> {a.description}{' '}
                <em>
                  To hit: {a.to_hit_bonus}, DC {a.save_dc}, {a.damage} {a.damage_type}
                  {String(a.can_multiattack || '').toLowerCase() === 'true' && ' (Multiattack)'}
                </em>
              </p>
            ))}
          </>
        )}
        {arr(m.spells).length > 0 && (
          <>
            <hr />
            <p>
              <strong>Spells</strong>
            </p>
            {arr(m.spells).map((s, i) => (
              <p key={i}>
                <strong>{s.name}.</strong> {s.description}{' '}
                {(s.to_hit_bonus || s.save_dc || s.damage) && (
                  <em>
                    {[s.to_hit_bonus && `To hit: ${s.to_hit_bonus}`, s.save_dc && String(s.save_dc).toUpperCase() !== 'NA' && `DC ${s.save_dc}${s.save_stat ? ` ${s.save_stat}` : ''}`, s.damage && `${s.damage}${s.damage_type ? ` ${s.damage_type}` : ''}`].filter(Boolean).join(', ')}
                  </em>
                )}
              </p>
            ))}
          </>
        )}
        {arr(m.legendary_actions).length > 0 && (
          <>
            <hr />
            <p>
              <strong>Legendary Actions</strong>
            </p>
            {arr(m.legendary_actions).map((la, i) => (
              <p key={i}>
                <strong>
                  {la.name} ({la.uses}).
                </strong>{' '}
                {la.description}{' '}
                {(la.to_hit_bonus || la.save_dc || la.damage) && (
                  <em>
                    {[la.to_hit_bonus && `To hit: ${la.to_hit_bonus}`, la.save_dc && String(la.save_dc).toUpperCase() !== 'NA' && `DC ${la.save_dc}${la.save_stat ? ` ${la.save_stat}` : ''}`, la.damage && `${la.damage}${la.damage_type ? ` ${la.damage_type}` : ''}`].filter(Boolean).join(', ')}
                  </em>
                )}
              </p>
            ))}
          </>
        )}
      </div>

      {error && (
        <div className="notification is-warning mt-3">{error}</div>
      )}

      <div className="buttons mt-4">
        {needsAuth && (
          <p className="mr-2">
            <button type="button" className="button is-small is-warning" onClick={signInWithGoogle}>
              Sign in to edit or save
            </button>
          </p>
        )}
        {showEdit && (
          <form onSubmit={handleEdit} className="is-inline-block">
            <div className="field has-addons">
              <div className="control">
                <input
                  className="input"
                  type="text"
                  placeholder="e.g. add fire immunity, increase AC to 18"
                  value={editPrompt}
                  onChange={(e) => setEditPrompt(e.target.value)}
                  disabled={editLoading}
                />
              </div>
              <div className="control">
                <button type="submit" className="button" disabled={editLoading || !editPrompt.trim()}>
                  {editLoading ? 'Editing...' : 'Reprompt edit'}
                </button>
              </div>
            </div>
          </form>
        )}
        {showSave && (
          <button
            className="button is-primary"
            onClick={handleSave}
            disabled={saveLoading}
          >
            {saveLoading ? 'Saving...' : 'Save to Supabase'}
          </button>
        )}
        <Link to="/monsters" className="button">
          Back to list
        </Link>
      </div>
    </div>
  );
}

export default MonsterStatBlock;
